import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Ticket } from "lucide-react";

const STORAGE_KEY = "betmar_promo_popup_seen_v3";
const IMAGE_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_landing-preview-144/artifacts/agita9ae_popup%20mirella.png";

export default function PromoPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show once per session, with a short delay so the hero can render first
    if (sessionStorage.getItem(STORAGE_KEY) === "1") return;
    const t = setTimeout(() => setOpen(true), 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    // Prevent body scroll while modal open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const goToPicker = () => {
    close();
    setTimeout(() => {
      document.getElementById("elegir-ficha")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6"
          data-testid="promo-popup"
          onClick={close}
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-[420px] sm:max-w-[460px] max-h-[92vh] flex flex-col rounded-2xl sm:rounded-3xl overflow-hidden border-2 border-[#F0B91B]/60 bg-[#0A2818] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.9)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button — always visible */}
            <button
              onClick={close}
              aria-label="Cerrar promoción"
              className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/85 text-white flex items-center justify-center backdrop-blur-md border border-white/20 transition-colors"
              data-testid="promo-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Poster image — object-contain so nothing gets cropped */}
            <div className="relative w-full bg-[#0A2818] flex-1 min-h-0 overflow-hidden">
              <img
                src={IMAGE_URL}
                alt="Promoción Gran Proyecto BETMAR — Chevrolet Spark Life 2015 y Lote 208m²"
                className="w-full h-full object-contain"
                loading="eager"
                draggable={false}
              />
            </div>

            {/* CTA strip pinned at the bottom */}
            <div className="border-t-2 border-[#F0B91B]/50 bg-[#051A0F]">
              <button
                onClick={goToPicker}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-4 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] font-bebas text-base sm:text-lg tracking-wider transition-colors"
                data-testid="promo-cta-pick"
              >
                <Ticket className="w-5 h-5" />
                Elegir mi ficha ahora
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
